import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../../Assets/magi.png';
import { FaBuilding, FaLock, FaFileInvoiceDollar, FaSignOutAlt, FaChevronDown, FaEllipsisV, FaUserCheck } from 'react-icons/fa';

const NAV_ITEMS = [
  { to: '/corporateData', label: 'Clients', shortLabel: 'Clients', icon: FaBuilding, accentColor: '#0284c7', match: (p) => p === '/corporateData' || p === '/' || p === '/landing' },
  { to: '/invoice', label: 'Invoice System', shortLabel: 'Invoice', icon: FaFileInvoiceDollar, accentColor: '#dc2626', match: (p) => p === '/invoice' },
];

const ALL_NAV_ITEMS = [
  ...NAV_ITEMS,
];

const MORE_ITEMS = [];

const getInitials = (name) => (name ? name.split('@')[0].substring(0, 2).toUpperCase() : 'U');

const AccountMenu = ({ displayUserName, rawUserName, onLogout, size = 'md', align = 'end' }) => {
  const dims = size === 'sm' ? 32 : 40;
  const fontSize = size === 'sm' ? 12 : 14;

  if (size === 'sm') {
    return (
      <div className="dropdown dropup flex-fill d-flex justify-content-center">
        <button
          className="bottom-nav-item-premium w-100"
          type="button"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          aria-label={`Account menu for ${displayUserName}`}
        >
          <div className="bottom-nav-icon-wrapper">
            <div className="user-avatar-premium" style={{ width: dims, height: dims, fontSize }}>
              {getInitials(displayUserName)}
              <span className="user-status-dot" />
            </div>
          </div>
          <span>Account</span>
        </button>
        <ul className={`dropdown-menu dropdown-menu-${align} premium-dropdown-menu mb-2`}>
          <li className="px-3 py-3 border-bottom mb-2 account-card">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="status-badge-pill"><FaUserCheck size={10} /> Active</span>
            </div>
            <div className="fw-bold text-dark">{displayUserName}</div>
            <div className="small text-muted text-truncate" style={{ maxWidth: '200px' }}>{rawUserName}</div>
          </li>
          <li className="px-2">
            <button className="dropdown-item signout-item py-2 fw-medium d-flex align-items-center gap-2 rounded-3" onClick={onLogout}>
              <FaSignOutAlt /> Sign Out
            </button>
          </li>
        </ul>
      </div>
    );
  }

  return (
    <div className="dropdown">
      <button
        className="btn p-0 border-0 shadow-none d-flex align-items-center gap-2 account-trigger"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        aria-label={`Account menu for ${displayUserName}`}
      >
        <div className="user-avatar-premium" style={{ width: dims, height: dims, fontSize }}>
          {getInitials(displayUserName)}
          <span className="user-status-dot" />
        </div>
        <div className="d-none d-lg-flex flex-column align-items-start text-start me-1">
          <span className="fw-bold text-dark account-name" style={{ lineHeight: 1.2 }}>{displayUserName}</span>
          <span className="text-muted" style={{ fontSize: '11px', lineHeight: 1 }}>Account</span>
        </div>
        <FaChevronDown size={10} className="text-muted opacity-75 d-none d-lg-inline" />
      </button>
      <ul className={`dropdown-menu dropdown-menu-${align} premium-dropdown-menu mt-2 shadow-lg`}>
        <li className="px-3 py-3 border-bottom mb-2 account-card">
          <div className="d-flex align-items-center gap-2 mb-1">
            <span className="status-badge-pill"><FaUserCheck size={10} /> Authenticated</span>
          </div>
          <div className="fw-bold text-dark">{displayUserName}</div>
          <div className="small text-muted text-truncate" style={{ maxWidth: '210px' }}>{rawUserName}</div>
        </li>
        <li className="px-2">
          <button className="dropdown-item signout-item py-2 fw-semibold d-flex align-items-center gap-2 rounded-3" onClick={onLogout}>
            <FaSignOutAlt /> Sign Out
          </button>
        </li>
      </ul>
    </div>
  );
};

const MoreMenu = ({ variant = 'desktop', location }) => (
  <ul className="dropdown-menu premium-dropdown-menu mt-2">
    {variant === 'mobile' && (
      <li className="px-3 pt-2 pb-1"><h6 className="dropdown-header px-0 more-menu-heading">Tools</h6></li>
    )}
    {MORE_ITEMS.map((item) => {
      const isMoreLinkActive = !item.external && location.pathname.startsWith(item.to.split('-')[0]);

      const Icon = item.icon;
      const content = (
        <>
          <Icon className="me-3 more-item-icon" /> {item.label}
        </>
      );
      return (
        <li className="px-2" key={item.label}>
          {item.external ? (
            <a
              className={`dropdown-item ${variant === 'mobile' ? 'py-3 fw-semibold fs-6' : 'py-2 fw-medium'} d-flex align-items-center rounded-3`}
              href={item.to}
              target="_blank"
              rel="noopener noreferrer"
            >
              {content}
            </a>
          ) : (
            <Link
              className={`dropdown-item ${variant === 'mobile' ? 'py-3 fw-semibold fs-6' : 'py-2 fw-medium'} d-flex align-items-center rounded-3 ${isMoreLinkActive ? 'active' : ''}`}
              to={item.to}
            >
              {content}
            </Link>
          )}
        </li>
      );
    })}
  </ul>
);

const DataPageHeader = ({ title, description, account, onLogin, onLogout, msalInstance, isInitialized, refreshTrigger, theme }) => {
  const location = useLocation();

  const activeNavItem = ALL_NAV_ITEMS.find(item => item.match(location.pathname));
  const isMoreItemActive = activeNavItem && !NAV_ITEMS.some(navItem => navItem.to === activeNavItem.to);

  const rawUserName = account?.username || account?.name || 'User';
  const displayUserName = account?.name || account?.username || 'User';

  return (
    <>
      <style>{`
        /* --- DESKTOP HEADER --- */
        @media print { .header-card-premium, .mobile-bottom-nav-premium, .dialer-offcanvas { display: none !important; } }

        .header-card-premium {
          position: relative;
          z-index: 1040;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid rgba(226, 232, 240, 0.8);
          border-radius: 1.5rem;
          padding: 1.2rem 2rem 1.25rem;
          box-shadow: 0 10px 30px -5px rgba(15, 23, 42, 0.07), 0 4px 12px -2px rgba(15, 23, 42, 0.03);
          margin-bottom: 2rem;
          overflow: visible;
        }

        .header-card-premium::before {
          content: '';
          position: absolute;
          top: 0;
          left: 2rem;
          right: 2rem;
          height: 3px;
          border-radius: 9999px;
          background: linear-gradient(90deg, #0284c7 0%, #2563eb 50%, #dc2626 100%);
        }

        .header-top-row {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          position: relative;
        }

        .header-brand {
          align-self: stretch;
          display: flex;
          align-items: center;
          transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .header-brand:hover {
          transform: translateY(-2px);
        }

        .header-brand img {
          height: 64px;
          filter: drop-shadow(0 4px 12px rgba(15, 23, 42, 0.08));
        }

        @media (max-width: 991.98px) {
          .header-brand img { height: 46px; }
        }

        /* --- CENTERED TITLE BAR --- */
        .header-title-row {
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 1.25rem;
          padding-top: 1.1rem;
          border-top: 1px solid #f1f5f9;
          width: 100%;
        }

        .header-title-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          padding: 8px 24px;
          border-radius: 9999px;
          box-shadow: 0 4px 16px -2px rgba(15, 23, 42, 0.06), inset 0 1px 0 rgba(255,255,255,0.9);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .header-title-pill:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px -2px rgba(15, 23, 42, 0.09);
        }

        .header-title {
          font-weight: 700;
          font-size: 1.2rem;
          letter-spacing: -0.02em;
        }

        .title-icon-badge {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1e293b 0%, #0284c7 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 3px 8px rgba(2, 132, 199, 0.3);
        }

        .user-avatar-premium {
          background: linear-gradient(135deg, #0f172a 0%, #0284c7 100%);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 8px rgba(2, 132, 199, 0.25);
          position: relative;
          flex-shrink: 0;
        }

        .user-status-dot {
          position: absolute;
          bottom: -1px;
          right: -1px;
          width: 10px;
          height: 10px;
          background-color: #10b981;
          border: 2px solid #ffffff;
          border-radius: 50%;
        }

        .status-badge-pill {
          background: #dcfce7;
          color: #15803d;
          font-size: 10.5px;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 9999px;
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }

        .account-trigger {
          background: #ffffff;
          border: 1px solid #e2e8f0 !important;
          border-radius: 9999px;
          padding: 4px 12px 4px 4px !important;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
          transition: all 0.2s ease;
        }
        .account-trigger:hover {
          background: #f8fafc;
          border-color: #cbd5e1 !important;
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
        }

        .account-name { font-size: 13px; }

        .account-card { background: #f8fafc; border-radius: 0.75rem 0.75rem 0 0; }

        .signout-item { color: #dc2626; }
        .signout-item:hover, .signout-item:focus { background: #fef2f2; color: #dc2626; }

        .dialer-btn-premium {
          border-radius: 9999px;
          padding: 8px 20px;
          font-size: 13.5px;
          font-weight: 600;
          border: none;
          color: #fff;
          box-shadow: 0 2px 8px rgba(13, 148, 136, 0.25);
          transition: all 0.2s ease;
        }
        .dialer-btn-premium.is-open { background: linear-gradient(135deg, #dc2626, #b91c1c); }
        .dialer-btn-premium.is-closed { background: linear-gradient(135deg, #0d9488, #059669); }
        .dialer-btn-premium:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 14px rgba(13, 148, 136, 0.35);
          color: #fff;
        }
        .dialer-btn-premium:active { transform: translateY(0); }

        .signin-btn-premium {
          background: linear-gradient(135deg, #1e40af, #2563eb);
          border: none;
          color: #fff;
          font-weight: 600;
          border-radius: 9999px;
          padding: 9px 24px;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
          transition: all 0.2s ease;
        }
        .signin-btn-premium:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 14px rgba(37, 99, 235, 0.35);
          color: #fff;
        }

        /* --- SEGMENTED CONTROL DESKTOP NAV --- */
        .header-nav-segmented {
          display: inline-flex;
          align-items: center;
          background: #f1f5f9;
          padding: 4px;
          border-radius: 9999px;
          border: 1px solid #e2e8f0;
          gap: 4px;
        }

        .nav-link-segmented {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 18px;
          border-radius: 9999px;
          font-weight: 600;
          font-size: 13.5px;
          text-decoration: none;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .nav-link-segmented .icon-container {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          transition: all 0.2s ease;
        }

        .nav-link-segmented.active {
          color: #0f172a;
          background: #ffffff;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04);
        }

        .nav-link-segmented.active .icon-container {
          background: #2563eb;
          color: #ffffff;
        }

        .nav-link-segmented.inactive {
          color: #64748b;
        }
        .nav-link-segmented.inactive .icon-container {
          background: transparent;
          color: #64748b;
        }
        .nav-link-segmented.inactive:hover {
          color: #0f172a;
          background: rgba(255, 255, 255, 0.6);
        }
        .nav-link-segmented.inactive:hover .icon-container {
          color: #2563eb;
        }

        .premium-dropdown-menu {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
          border-radius: 0.85rem;
          padding: 0.4rem;
        }

        .more-menu-heading { font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #94a3b8; }
        .more-item-icon { color: #2563eb; }

        .dropdown-item:hover, .dropdown-item:focus { background: #f8fafc; color: #0f172a; }

        /* --- MOBILE BOTTOM NAV --- */
        .mobile-bottom-nav-premium {
          position: fixed;
          bottom: 16px;
          left: 14px;
          right: 14px;
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          box-shadow: 0 8px 32px rgba(15, 23, 42, 0.12);
          border: 1px solid rgba(226, 232, 240, 0.8);
          border-radius: 9999px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 10px;
          z-index: 1050;
        }

        .bottom-nav-item-premium {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #64748b;
          text-decoration: none;
          font-size: 10.5px;
          font-weight: 600;
          gap: 3px;
          flex: 1;
          background: transparent;
          border: none;
          padding: 4px 0;
          transition: all 0.2s ease;
        }

        .bottom-nav-icon-wrapper {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          font-size: 16px;
          transition: all 0.2s ease;
        }

        .bottom-nav-item-premium.active .bottom-nav-icon-wrapper {
          background: linear-gradient(135deg, #1e40af, #2563eb);
          color: #ffffff;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
        }
        .bottom-nav-item-premium.active span { color: #0f172a; font-weight: 700; }

        @media (max-width: 991.98px) {
          body { padding-bottom: 86px; }
          .header-card-premium { padding: 0.9rem 1rem; border-radius: 1rem; margin-bottom: 1.25rem; }
          .header-top-row {
            flex-wrap: nowrap;
            gap: 0.75rem;
          }
          .header-title-row {
             margin-top: 0.85rem;
             padding-top: 0.85rem;
          }
          .desktop-only { display: none !important; }
        }

        @media (min-width: 992px) {
          .mobile-only { display: none !important; }
        }

        @media (prefers-reduced-motion: reduce) {
          .dialer-btn-premium, .signin-btn-premium, .account-trigger, .nav-link-segmented { transition: none; }
        }
      `}</style>

      <header className="header-card-premium">
        <div className="header-top-row">
          <div className="header-brand" style={{ zIndex: 1 }}>
            <Link to="/" className="text-decoration-none d-flex align-items-center">
              <img src={Logo} alt="Magizhini Technology Services Logo" />
            </Link>
          </div>

          <div className="d-none d-lg-flex justify-content-center flex-grow-1">
            <nav className="header-nav-segmented" aria-label="Main Navigation">
              {NAV_ITEMS.map(({ to, label, icon: Icon, accentColor, match }) => {
                const isActive = match(location.pathname);
                return (
                  <Link
                    key={to}
                    to={to}
                    className={`nav-link-segmented ${isActive ? 'active' : 'inactive'}`}
                  >
                    <div
                      className="icon-container"
                      style={isActive ? { background: `linear-gradient(135deg, ${accentColor}, #1d4ed8)` } : {}}
                    >
                      <Icon size={13} />
                    </div>
                    <span>{label}</span>
                  </Link>
                );
              })}

              {MORE_ITEMS.length > 0 && (
                <div className="dropdown ms-1">
                  <button className={`btn btn-link text-decoration-none nav-link-segmented inactive border-0 d-flex align-items-center gap-2 ${isMoreItemActive ? 'active' : ''}`} type="button" data-bs-toggle="dropdown" aria-expanded="false">
                    <span>More</span> <FaChevronDown size={9} style={{ opacity: 0.7 }} />
                  </button>
                  <MoreMenu variant="desktop" location={location} />
                </div>
              )}
            </nav>
          </div>

          <div className="action-block d-flex align-items-center justify-content-end gap-3" style={{ zIndex: 1 }}>
            {account ? (
                <div className="desktop-only">
                  <AccountMenu displayUserName={displayUserName} rawUserName={rawUserName} onLogout={onLogout} size="md" align="end" />
                </div>
            ) : (
              <button className="btn signin-btn-premium d-flex align-items-center gap-2" onClick={onLogin}>
                <FaLock size={13} />
                <span className="desktop-only">Sign In</span>
              </button>
            )}
          </div>
        </div>



      </header>

      <nav className="mobile-bottom-nav-premium mobile-only" aria-label="Primary">
        {NAV_ITEMS.map(({ to, shortLabel, icon: Icon, match }) => (
          <Link key={to} to={to} className={`bottom-nav-item-premium ${match(location.pathname) ? 'active' : ''}`}>
            <div className="bottom-nav-icon-wrapper"><Icon /></div>
            <span>{shortLabel}</span>
          </Link>
        ))}

        {MORE_ITEMS.length > 0 && (
          <div className="dropdown dropup flex-fill d-flex justify-content-center">
            <button className={`bottom-nav-item-premium w-100 ${isMoreItemActive ? 'active' : ''}`} type="button" data-bs-toggle="dropdown" aria-expanded="false">
              <div className="bottom-nav-icon-wrapper"><FaEllipsisV /></div>
              <span>More</span>
            </button>
            <MoreMenu variant="mobile" location={location} />
          </div>
        )}

        {account ? (
          <AccountMenu displayUserName={displayUserName} rawUserName={rawUserName} onLogout={onLogout} size="sm" align="end" />
        ) : (
          <button className="bottom-nav-item-premium w-100" onClick={onLogin}>
            <div className="bottom-nav-icon-wrapper" style={{ color: '#2563eb' }}><FaLock /></div>
            <span style={{ color: '#2563eb' }}>Sign In</span>
          </button>
        )}
      </nav>
    </>
  );
};

export default DataPageHeader;